import { PriorityHigh, WarningTriangle } from 'iconoir-react';
import moment from 'moment';

import i18n from '~/common/helpers/i18n';

export type DeploymentIssueSeverity = 'warning' | 'danger';

/**
 * The problems the details sidesheet raises an alert for, minus `pipelineUpdateProject`: that field
 * is read by the legacy sidesheet code but exists in no GraphQL query or schema, so a rule for it
 * here would never fire.
 *
 * The Run listing raises a narrower set — see `LISTING_ISSUE_KINDS`.
 */
export type DeploymentIssueKind =
  | 'outdatedGlobal'
  | 'outdatedAccount'
  | 'errorsAndIssues'
  | 'outOfMemory'
  | 'statusAlert';

export type DeploymentIssue = {
  kind: DeploymentIssueKind;
  /** Orders the list and picks icon and colour — an error outranks a warning. */
  severity: DeploymentIssueSeverity;
  /** Short word, so the tooltip list is scannable. */
  label: string;
  /** What is wrong and what to do about it — the whole sentence. */
  description: string;
};

/**
 * Structural subset of a deployment the rules below read.
 *
 * `lastError` is not one of them, and is kept on purpose: the tests pass it to prove the error rule
 * ignores it, which is what stops the staleness window from being reattached to a problem the
 * deployment's own status already dates.
 */
export type DeploymentIssueInput = {
  status?: { text?: string } | null;
  metadata?: {
    errorOutdatedGlobal?: string;
    errorOutdatedAccount?: string;
  } | null;
  errorCount?: string | number | null;
  oomCount?: string | number | null;
  lastError?: string | null;
  lastOMM?: string | null;
};

/** Same window the deployment card has always used to stop reporting an old out of memory. */
const STALE_OOM_DAYS = 7;

/**
 * Errors lead, warnings follow. This is the one order every surface reports problems in — the
 * listing tooltip and the sidesheet alerts both read it off `getDeploymentIssues`, so a deployment
 * cannot list its problems one way on the card and another way once opened. The listing shows fewer
 * of them, never different ones, and never in another order.
 */
const SEVERITY_RANK: Record<DeploymentIssueSeverity, number> = {
  danger: 0,
  warning: 1
};

/**
 * Resolved through thunks so the i18n keys stay literals — which is what the typed resources
 * require — and so the lookup happens after the language is detected.
 *
 * Three of these come from the Monitor Insights chips (`scenes.monitor.overview.labels.chip_*`).
 * Insights already flags an edited account on the overview table with exactly this wording, and
 * two different words for the same problem in two screens is worse than the coupling.
 */
const LABEL: Record<DeploymentIssueKind, () => string> = {
  outdatedGlobal: () =>
    i18n.t('scenes.runtime.labels.issue_chip_global') as string,
  outdatedAccount: () =>
    i18n.t('scenes.monitor.overview.labels.chip_account') as string,
  errorsAndIssues: () =>
    i18n.t('scenes.monitor.overview.labels.chip_errors') as string,
  outOfMemory: () =>
    i18n.t('scenes.monitor.overview.labels.chip_memory') as string,
  // Only reachable for a degraded deployment — an errored one is left to the status chip.
  statusAlert: () => i18n.t('common.labels.degraded') as string
};

/**
 * The sentence shown on hover. Each one says what happened *and* what to do, which is why these
 * point at the details copy rather than at the generic card messages: "Your pipeline has an
 * issue. Learn more here." names no problem and offers no way out — and in a tooltip there is
 * nothing to click, so the promise of a link is broken too.
 */
const DESCRIPTION: Record<DeploymentIssueKind, () => string> = {
  outdatedGlobal: () =>
    i18n.t(
      'label.update_globals_redeploy_needed_card_details_msg_warning'
    ) as string,
  outdatedAccount: () =>
    i18n.t(
      'label.update_accounts_redeploy_needed_card_details_msg_warning'
    ) as string,
  errorsAndIssues: () =>
    i18n.t('label.error-message-pipeline-runtime') as string,
  outOfMemory: () =>
    i18n.t('scenes.runtime.messages.error.out_of_memory') as string,
  statusAlert: () => i18n.t('scenes.runtime.messages.error.degraded') as string
};

/**
 * The two icons the design gives a deployment problem, wherever it is shown: the listing
 * indicator, its tooltip and the alerts in the details drawer. A danger is the priority diamond,
 * a warning is the triangle.
 */
export const DEPLOYMENT_ISSUE_ICON: Record<
  DeploymentIssueSeverity,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  danger: PriorityHigh,
  warning: WarningTriangle
};

const buildIssue = (
  kind: DeploymentIssueKind,
  severity: DeploymentIssueSeverity
): DeploymentIssue => ({
  kind,
  severity,
  label: LABEL[kind](),
  description: DESCRIPTION[kind]().trim()
});

/** An out of memory older than the window stops being reported, as the card always did. */
const isStale = (timestamp?: string | null) => {
  if (!timestamp) {
    return false;
  }

  return moment().isSameOrAfter(
    moment(timestamp, 'x').add(STALE_OOM_DAYS, 'days')
  );
};

/**
 * Every problem worth flagging on a deployment, most severe first.
 *
 * `checkErrorDeployment` — which the sidesheet's summary alert uses — reports only the first
 * match of a fixed chain, so a deployment with an edited Global *and* out of memory mentions one
 * and hides the other. In a listing that is the wrong trade: the point of the indicator is to say
 * what needs attention before the card is opened, and "one of the reasons" is not enough to
 * decide whether to open it. Each condition is evaluated on its own here.
 *
 * Two things are deliberately left out. A deployment being deleted gets nothing — a redeploy
 * notice on something leaving the air is noise. An errored deployment gets no status badge, since
 * the status chip beside it already reads "Errors".
 */
export const getDeploymentIssues = (
  deployment?: DeploymentIssueInput | null
): DeploymentIssue[] => {
  if (!deployment) {
    return [];
  }

  const status = (deployment.status?.text ?? '').toLowerCase();

  if (status === 'deleting') {
    return [];
  }

  const issues: DeploymentIssue[] = [];

  if (deployment.metadata?.errorOutdatedAccount) {
    issues.push(buildIssue('outdatedAccount', 'warning'));
  }

  if (deployment.metadata?.errorOutdatedGlobal) {
    issues.push(buildIssue('outdatedGlobal', 'warning'));
  }

  // Only while the status says the deployment is failing right now, which is the gate the sidesheet
  // applied before this problem reached the listing at all. `error-count` counts infrastructure
  // events — a replica that failed and was restarted — it is cumulative, and the controller resets
  // it on nothing short of a redeploy, so on a deployment that is up it is history rather than a
  // problem. No staleness window here on purpose: the status gate already answers "is it failing
  // now", and a window would leave a deployment stuck in Error for longer than it with no alert.
  // `Number(...)`: errorCount arrives from GraphQL as a string, and '0' is truthy.
  if (
    (status === 'error' || status === 'degraded') &&
    Number(deployment.errorCount) > 0
  ) {
    issues.push(buildIssue('errorsAndIssues', 'danger'));
  }

  if (Number(deployment.oomCount) > 0 && !isStale(deployment.lastOMM)) {
    issues.push(buildIssue('outOfMemory', 'warning'));
  }

  // `degraded` only: the status chip labels it "Starting", which hides it entirely.
  if (status === 'degraded') {
    issues.push(buildIssue('statusAlert', 'warning'));
  }

  return issues.sort(
    (left, right) =>
      SEVERITY_RANK[left.severity] - SEVERITY_RANK[right.severity]
  );
};

/** One indicator standing for every problem the deployment has. */
export type DeploymentIssueSummary = {
  /** The worst severity among the problems — the icon and colour the indicator takes. */
  severity: DeploymentIssueSeverity;
  count: number;
  /** What the accessible name carries, so nothing here is hover-only. */
  description: string;
  /** Every problem, worst first — what the tooltip lists. */
  issues: DeploymentIssue[];
};

/**
 * Collapses the problem list into a single indicator.
 *
 * One badge per problem does not survive the real screen: a deployment that is degraded, out of
 * memory, erroring and waiting on two edited assets grows a badge per problem, they wrap onto a
 * second and third line and the card footer stops being readable. The listing only has to answer
 * "does this need attention, and how badly" — the breakdown belongs in the tooltip and in the
 * sidesheet.
 */
export const summarizeDeploymentIssues = (
  issues: DeploymentIssue[]
): DeploymentIssueSummary | null => {
  const [worst] = issues;

  if (!worst) {
    return null;
  }

  // One problem is its own sentence; several are announced by the count and then listed.
  // `total` and not `count`: `count` is i18next's plural trigger, which would send the lookup
  // after `issue_count_one` / `_other` keys that do not exist.
  const description =
    issues.length === 1
      ? worst.description
      : `${i18n.t('scenes.runtime.labels.issue_count', {
          total: issues.length
        })}. ${issues.map(issue => issue.description).join(' ')}`;

  return {
    severity: worst.severity,
    count: issues.length,
    description,
    issues
  };
};

/**
 * The problems the Run listing raises — a narrower set than the sidesheet's.
 *
 * `errorsAndIssues` is deliberately not one of them. Every other problem here names something the
 * user can do about it: redeploy the pipeline, or deploy it at a greater size. That one names
 * nothing. Its count comes from a replica that failed and was restarted — an infrastructure event,
 * reported by three different Kubernetes watchers that collapse into a single integer with no
 * reason attached, so it cannot tell an edited configuration apart from a node the platform lost.
 * As a red indicator on a deployment that is up and serving it reads as an emergency the user has
 * no way to answer, and teaches them to ignore the four indicators beside it that they can.
 *
 * It still raises an alert in the details sidesheet, on a deployment whose status already says it
 * is failing — the same place, and the same condition, it had before the listing had an indicator.
 */
const LISTING_ISSUE_KINDS = new Set<DeploymentIssueKind>([
  'outdatedGlobal',
  'outdatedAccount',
  'outOfMemory',
  'statusAlert'
]);

/** Convenience for the call sites that hold a deployment rather than a problem list. */
export const getDeploymentIssueSummary = (
  deployment?: DeploymentIssueInput | null
): DeploymentIssueSummary | null =>
  summarizeDeploymentIssues(
    getDeploymentIssues(deployment).filter(issue =>
      LISTING_ISSUE_KINDS.has(issue.kind)
    )
  );
